import React, { useState } from 'react';
import { DateParam, NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params';
import moment from 'moment';
import {Box, Button, Grid, MenuItem, Stack, TextField} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DatePicker } from '@mui/x-date-pickers';

import { AppBar, DebouncedTextField, Empty, Error, Loader } from '@/components';
import { OrderDirections, ORDER_DIRECTIONS } from '@/utils/order-directions';
import { CASHBOX_OPERATIONS } from '@/utils/cashbox-operations';

import { CashboxOperationsTable, CreateCashboxOperation } from './components';
import {useLazyGetCashboxOperationsQuery} from "@/services/cashbox-operation";

const CashboxOperationsPage: React.FC = () => {
  const [{ search, order_direction, page, date_from, date_to, status }, setQuery] =
      useQueryParams({
        search: withDefault(StringParam, ''),
        page: withDefault(NumberParam, 1),
        order_direction: withDefault(StringParam, OrderDirections.DESC),
        status: withDefault(StringParam, ''),
        date_from: withDefault(DateParam, moment().toDate()),
        date_to: withDefault(DateParam, moment().toDate()),
      });

  const [trigger, { data: cashboxOperations, isLoading, isFetching, isError, isSuccess }] =
      useLazyGetCashboxOperationsQuery();

  const [hasSearched, setHasSearched] = useState(false);
  const [createModalOpened, setOpenedCreateModal] = useState(false);

  const buildRequest = (overridePage = page) => ({
    params: {
      search: search || null,
      order_direction,
      page: overridePage,
      status: status || null,
      order_by: 'id',
      date_from: date_from ? moment(date_from).format('YYYY-MM-DD') : null,
      date_to: date_to ? moment(date_to).format('YYYY-MM-DD') : null,
    },
  });

  const onSearch = () => {
    setQuery({ page: 1 });
    setHasSearched(true);
    trigger(buildRequest(1));
  };

  const onChangePage = (newPage: number) => {
    setQuery({ page: newPage });
    trigger(buildRequest(newPage));
  };

  return (
      <>
        <AppBar title="Касса (Внеломбардный приход/расход)" />

        <Grid container spacing={3} className="no-print">
          <Grid item xs={12}>
            <Stack direction="row" spacing={3} alignItems="flex-start">
              <Grid container spacing={2}>
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DebouncedTextField
                      type="search"
                      name="search"
                      label="Поиск"
                      placeholder="Введите значение"
                      value={search}
                      onChange={e => setQuery({ search: e.target.value, page: 1 })}
                      variant="filled"
                      fullWidth
                      size="small"
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <TextField
                      select
                      value={order_direction}
                      label="Сортировать по"
                      disabled={isLoading}
                      onChange={e => setQuery({ order_direction: e.target.value })}
                      variant="filled"
                      fullWidth
                      size="small"
                  >
                    {ORDER_DIRECTIONS.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <TextField
                      select
                      value={status}
                      label="Вид операции"
                      disabled={isLoading}
                      onChange={e => setQuery({ status: e.target.value })}
                      variant="filled"
                      fullWidth
                      size="small"
                  >
                    <MenuItem value="">Все</MenuItem>
                    {CASHBOX_OPERATIONS.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DatePicker
                      mask="__.__.____"
                      label="Дата от"
                      value={date_from}
                      minDate={moment(date_to).subtract(6, 'months')}
                      maxDate={moment(date_to)}
                      onChange={v => setQuery({ date_from: v ? moment(v).toDate() : undefined })}
                      disabled={isLoading || isFetching}
                      renderInput={params => (
                          <TextField {...params} name="date_from" variant="filled" size="small" fullWidth />
                      )}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DatePicker
                      mask="__.__.____"
                      label="Дата до"
                      value={date_to}
                      maxDate={moment()}
                      onChange={v => setQuery({ date_to: v ? moment(v).toDate() : undefined })}
                      disabled={isLoading || isFetching}
                      renderInput={params => (
                          <TextField {...params} name="date_to" variant="filled" size="small" fullWidth />
                      )}
                  />
                </Grid>

                {/* Кнопка Поиск */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <LoadingButton
                      loading={isFetching}
                      disabled={isFetching}
                      onClick={onSearch}
                      size="large"
                      variant="contained"
                      fullWidth
                  >
                    Поиск
                  </LoadingButton>
                </Grid>

                {/* Добавить операцию */}
              </Grid>
              <Box sx={{ width: 210 }}>
                <Button
                    disabled={isLoading}
                    onClick={() => setOpenedCreateModal(true)}
                    size="large"
                    variant="contained"
                    fullWidth
                >
                  Добавить
                </Button>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            {!hasSearched ? (
                <Empty text="Нажмите «Поиск», чтобы увидеть данные" />
            ) : isLoading ? (
                <Loader />
            ) : isError ? (
                <Error text="Произошла ошибка с загрузкой данных!" />
            ) : isSuccess && cashboxOperations?.data?.length ? (
                <CashboxOperationsTable
                    list={cashboxOperations}
                    page={page}
                    disabled={isFetching}
                    onChangePage={onChangePage}
                />
            ) : (
                <Empty text="Данные по кассе отсутствуют" />
            )}
          </Grid>
        </Grid>

        <CreateCashboxOperation
            open={createModalOpened}
            onClose={() => setOpenedCreateModal(false)}
        />
      </>
  );
};

export default CashboxOperationsPage;
